import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useRtl } from "@ryde/redux";
import {
  PLURAL_KEY,
  selectedRidesSelector,
  SINGLE_KEY,
  useFormats,
  type IPassengerInfo,
} from "@ryde/shared";

import {
  BulkPrintRideCustomFields,
  BulkPrintRideInfo,
  getPassengerPrintLine,
  getRideFlightCodes,
  getStationPassengers,
  PrintRidesBodyStyled,
  PrintRidesHeaderStyled,
  PrintRidesWrapperStyled,
  RIDE_BULK_PRINT,
  type IRidePrintLabels,
} from "./parts";

interface IBulkPrintRidesProps {
  labels: IRidePrintLabels;
}

export const BulkPrintRides = React.forwardRef<
  HTMLDivElement,
  IBulkPrintRidesProps
>(({ labels }, ref) => {
  const { t } = useTranslation();
  const { getFormattedOtherRideData, formatTimeHHmmNormalized } = useFormats();
  const { direction, isRtl } = useRtl();
  const selectedRides = useRecoilValue(selectedRidesSelector);
  const { areaNameById, flightCodeById, isFlightsEnabled } = labels;

  const getTypeByDirection = (
    rideDirection: string,
    stationId: number,
    passengersInfo: IPassengerInfo,
  ): JSX.Element => {
    const [pickup, dropoff] = [t("ride.PICKUP"), t("ride.DROPOFF")];

    if (rideDirection === "A") {
      if (passengersInfo.from === stationId) {
        return <div>{pickup}</div>;
      } else if (passengersInfo.to === stationId) {
        return <div>{dropoff}</div>;
      }
    } else {
      if (passengersInfo.to === stationId) {
        return <div>{dropoff}</div>;
      } else if (passengersInfo.from === stationId) {
        return <div>{pickup}</div>;
      }
    }

    return <div></div>;
  };

  const renderPassenger = (passenger: IPassengerInfo): JSX.Element => (
    <div key={`passenger_${passenger.id}`}>
      {getPassengerPrintLine(passenger)}
    </div>
  );

  const renderPassengerUnit = (passenger: IPassengerInfo): JSX.Element => (
    <div key={`passenger_unit_${passenger.id}`}>{passenger.unitName}</div>
  );

  return (
    <div ref={ref} style={{ direction }}>
      {selectedRides.map(ride => (
        <PrintRidesWrapperStyled key={`ride_${ride.id}`}>
          <PrintRidesHeaderStyled>
            <div>
              <span className="ride-number">
                {t("ride.formLabel.rideNumber")} {ride.id}
              </span>
              <span className="site-name">
                {[t(`site.${SINGLE_KEY}`), ride.siteName].join(
                  RIDE_BULK_PRINT.partsDelimiter,
                )}
              </span>
            </div>
            <div>
              <SVG_MUI.LogoAlternativeSVG />
            </div>
          </PrintRidesHeaderStyled>
          <PrintRidesBodyStyled>
            <BulkPrintRideInfo ride={ride} />
            <div className="table-block">
              <div className="table-header">
                <div className="label">{t(`station.${SINGLE_KEY}`)}</div>
                <div className="label">{t("general.TIME")}</div>
                <div className="label">{t("ride.formLabel.rideTypeId")}</div>
                <div className="label">{t("booking.formLabel.unitId")}</div>
                <div className="label">
                  {getFormattedOtherRideData(
                    t("booking.formLabel.seats"),
                    t("booking.formLabel.passenger.phoneMobile"),
                    t(`passenger.${PLURAL_KEY}`),
                    isRtl,
                  )}
                </div>
              </div>
              <div className="table-body">
                {ride.metadata?.stationsInfo?.map(station => {
                  const areaName = station.areaId
                    ? areaNameById.get(station.areaId)
                    : undefined;
                  const stationPassengers = getStationPassengers({
                    stationId: station.id,
                    passengersInfo: ride.metadata?.passengersInfo,
                  });

                  return (
                    <div
                      key={`station_${station.id}_${ride.id}`}
                      className="table-body-block"
                    >
                      <div>
                        {station.name}
                        {areaName ? ` (${areaName})` : ""}
                      </div>
                      <div className="bold">
                        {formatTimeHHmmNormalized(station.arrivalTime)}
                      </div>
                      {/* TODO : change according to date & time format */}
                      <div className="pickup-dropoff-list">
                        {stationPassengers.map(passenger => (
                          <Fragment key={`${passenger.id}_${ride.id}`}>
                            {getTypeByDirection(
                              ride.direction,
                              station.id,
                              passenger,
                            )}
                          </Fragment>
                        ))}
                      </div>
                      <div className="unit-list">
                        {stationPassengers.map(passenger =>
                          renderPassengerUnit(passenger),
                        )}
                      </div>
                      <div className="passenger-list">
                        {stationPassengers.map(passenger =>
                          renderPassenger(passenger),
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <BulkPrintRideCustomFields
              flightCodes={getRideFlightCodes({
                flightIds: ride.metadata?.flightIds,
                flightCodeById,
              })}
              remarks={ride.metadata?.remarks}
              shouldShowFlights={isFlightsEnabled}
            />
          </PrintRidesBodyStyled>
        </PrintRidesWrapperStyled>
      ))}
    </div>
  );
});
